/**
 * Section audit panel READ contract — RO-3-00f / #44473.
 *
 * Port of `excellent/app/projects/_sections/section-audit-panel.tsx` (cause A).
 * The writes (run / fix / dismiss) already live on `otherActions.audit*` from
 * RO-2-11b. This op is the missing READ half: status + open findings + running
 * flag for one section, so the panel can self-populate through `useApi`.
 *
 * Self-heal (`reapStaleAuditRuns` + `reconcileAuditFixTasks`) runs in the
 * handler to match the Next wrapper — both are best-effort maintenance, not
 * the contract's subject.
 */

import { z } from "zod";
import type { Operation } from "../types";

const finding = z.object({
  id: z.number(),
  severity: z.enum(["high", "medium", "low"]),
  title: z.string(),
  detail: z.string().nullable(),
  fix_prompt: z.string().nullable(),
  status: z.string(),
});
export type SectionAuditFindingDto = z.infer<typeof finding>;

const panelResponse = z.object({
  /** False when the section has no profile, is not onboarded, or has no status. */
  visible: z.boolean(),
  sectionSlug: z.string(),
  sectionLabel: z.string().nullable(),
  focus: z.string().nullable(),
  score: z.number().nullable(),
  ageDays: z.number().nullable(),
  cadenceDays: z.number().nullable(),
  isDue: z.boolean(),
  running: z.boolean(),
  lastRunStatus: z.string().nullable(),
  findings: z.array(finding),
});
export type SectionAuditPanelResponse = z.infer<typeof panelResponse>;

const panelQuery = z.object({
  sectionSlug: z.string().min(1),
  workspaceId: z.coerce.number().int(),
  projectId: z.coerce.number().int().optional(),
  projectSlug: z.string().optional(),
});

const panelOp: Operation<void, z.infer<typeof panelQuery>, void, SectionAuditPanelResponse> = {
  method: "GET",
  path: () => "/api/audits/section-panel",
  query: panelQuery,
  response: panelResponse,
};

// ── hub — /audits list (RO-3-06 / #44299) ────────────────────────────────────
//
// Port of the server reads in `excellent/app/audits/page.tsx`:
//   reapStaleAuditRuns + reconcileAuditFixTasks + allSectionAuditStatuses
// filtered to onboarded. Each card still self-loads findings via
// `sectionAudit.panel`; the hub only needs slugs + due counts for the toolbar.

const hubSection = z.object({
  section_slug: z.string(),
  label: z.string(),
  onboarded: z.boolean(),
  isDue: z.boolean(),
  lastRunStatus: z.string().nullable(),
});
export type SectionAuditHubSection = z.infer<typeof hubSection>;

const hubResponse = z.object({
  workspaceId: z.number(),
  sections: z.array(hubSection),
  dueCount: z.number(),
});
export type SectionAuditHubResponse = z.infer<typeof hubResponse>;

const hubOp: Operation<void, void, void, SectionAuditHubResponse> = {
  method: "GET",
  path: () => "/api/audits/hub",
  response: hubResponse,
};

export const sectionAudit = {
  panel: panelOp,
  hub: hubOp,
};
